import React from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';

/**
 * The two clocks this feature genuinely needs, and can't collapse into
 * one without losing information:
 *
 * - `lead_local`: each lead's own local time zone. "9am" means 9am
 *   wherever that lead lives, never converted or blended into a single
 *   clock -- that's what lets Timing/Plan's demand curves show a real
 *   per-state pattern instead of a smeared, shifted one.
 * - `reference_clock`: one shared company clock (America/New_York today)
 *   that every agent's shift and every "who's on now" number is measured
 *   against, since staffing is a real, physical, single-timezone fact
 *   regardless of which state's leads an agent happens to be dialing.
 *
 * Fixed colors (not the tenant's brand color, which `primary`/`secondary`
 * both resolve to here) so the same two colors mean the same two things
 * on every tab, independent of branding -- this is the whole point: one
 * visual language, learned once, trusted everywhere `hour` or `now`
 * shows up on screen.
 *
 * 2026-08-12: rendered as plain colored text (icon + label, no pill/
 * border/background), not a `Chip` -- it used to be one, and every
 * section that shows this also shows `SectionScopeChip` right next to
 * it, which IS a chip. Two same-shaped pills with only an icon/color
 * difference read as "two of the same kind of badge" at a glance, even
 * though they answer unrelated questions (how recent the data is, vs.
 * what clock its hours are on). Downgrading this one to plain text keeps
 * the color coding (still the one thing to learn once) while making it
 * visually secondary, not a second competing badge.
 */
export type ClockBasis = 'lead_local' | 'reference_clock';

const TZ_ABBREV: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
};

const TZ_FULL_NAME: Record<string, string> = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
};

function tzAbbrev(tz: string): string {
  return TZ_ABBREV[tz] ?? tz;
}

function tzFullName(tz: string): string {
  return TZ_FULL_NAME[tz] ?? tz;
}

const COLORS = {
  lead_local: '#1565c0',
  reference_clock: '#7b1fa2',
} as const;

export default function ClockBadge({
  basis,
  referenceTz = 'America/New_York',
  size = 'small',
}: {
  basis: ClockBasis;
  referenceTz?: string;
  size?: 'small' | 'medium';
}) {
  const isLocal = basis === 'lead_local';
  const color = COLORS[basis];
  const label = isLocal ? "Their local time" : `Company clock (${tzAbbrev(referenceTz)})`;
  const tooltip = isLocal
    ? '\u201cHour\u201d here means each lead\u2019s own local time. For example, \u201c9am\u201d means 9am wherever that particular lead lives, not 9am at company headquarters. We keep it this way on purpose: converting everyone to one shared clock would hide real differences in when each state is actually reachable.'
    : `Every hour shown here uses one shared company clock: ${tzFullName(referenceTz)} (${tzAbbrev(referenceTz)}). This is the same clock for every agent's shift, no matter which state's leads they're actually calling.`;

  return (
    <Tooltip title={tooltip} arrow>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.375}
        sx={{ cursor: 'help', flexShrink: 0 }}
      >
        <ScheduleRounded sx={{ fontSize: size === 'small' ? 13 : 15, color }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: size === 'small' ? '0.6875rem' : '0.75rem',
            color,
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
